import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { 
  MessageCircle, 
  Search,
  Circle,
  Users
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeamMembersAvatars = ({ onOpenChat, refreshTrigger }) => {
  const { token, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [totalOnline, setTotalOnline] = useState(0);
  const [totalMembers, setTotalMembers] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadDmCount, setUnreadDmCount] = useState(0);
  const [conversations, setConversations] = useState([]);

  const fetchMembers = useCallback(async () => {
    try {
      const response = await fetch(`${API}/team/members`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache'  // Prevent browser caching
        }
      });
      if (response.ok) {
        const data = await response.json();
        const newMembers = data.online_members || [];
        const newTotalOnline = data.total_online || 0;
        const newTotalMembers = data.total_members || 0;
        
        // Always update state with fresh data from server
        // The is_online flag is time-sensitive and must always reflect current server state
        setMembers(newMembers);
        setTotalOnline(newTotalOnline);
        setTotalMembers(newTotalMembers);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  }, [token]);

  const fetchDmUnreadCount = useCallback(async () => {
    try {
      const response = await fetch(`${API}/dm/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const newCount = data.unread_count || 0;
        // Only update if changed
        setUnreadDmCount(prev => prev !== newCount ? newCount : prev);
      }
    } catch (error) {
      console.error('Error fetching DM unread count:', error);
    }
  }, [token]);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await fetch(`${API}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const newConversations = data || [];
        // Only update if changed
        setConversations(prev => JSON.stringify(prev) !== JSON.stringify(newConversations) ? newConversations : prev);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, [token]);

  // Send heartbeat to track online status
  const sendHeartbeat = useCallback(async () => {
    try {
      await fetch(`${API}/heartbeat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Heartbeat error:', error);
    }
  }, [token]);

  // Refresh when refreshTrigger changes (e.g., when messages are read)
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchDmUnreadCount();
      fetchConversations();
    }
  }, [refreshTrigger, fetchDmUnreadCount, fetchConversations]);

  useEffect(() => {
    fetchMembers();
    sendHeartbeat();
    fetchDmUnreadCount();
    fetchConversations();

    // Refresh members every 30 seconds
    const memberInterval = setInterval(fetchMembers, 30000);
    // Send heartbeat every minute
    const heartbeatInterval = setInterval(sendHeartbeat, 60000);
    // Check for new DMs every 10 seconds
    const dmInterval = setInterval(() => {
      fetchDmUnreadCount();
      fetchConversations();
    }, 10000);

    return () => {
      clearInterval(memberInterval);
      clearInterval(heartbeatInterval);
      clearInterval(dmInterval);
    };
  }, [fetchMembers, sendHeartbeat, fetchDmUnreadCount, fetchConversations]);

  const onlineMembers = members.filter(m => m.is_online);
  const displayMembers = onlineMembers.slice(0, 3);
  const extraCount = Math.max(0, totalOnline - 3);

  const filteredMembers = members.filter(m => 
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleStartChat = async (member) => {
    // Prevent starting chat with yourself
    if (member.id === user?.id) {
      toast.error('You cannot send a message to yourself');
      return;
    }
    if (onOpenChat) {
      onOpenChat(member);
      setShowModal(false);
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name) => {
    const colors = [
      'bg-red-500', 'bg-orange-500', 'bg-amber-500', 
      'bg-emerald-500', 'bg-teal-500', 'bg-cyan-500',
      'bg-blue-500', 'bg-indigo-500', 'bg-violet-500',
      'bg-purple-500', 'bg-fuchsia-500', 'bg-pink-500'
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Get unread count for a specific member from conversations
  const getUnreadCountForMember = (memberId) => {
    const conv = conversations.find(c => 
      c.participants.some(p => p.id === memberId)
    );
    return conv?.unread_count || 0;
  };

  return (
    <>
      {/* Avatar Stack - Shows up to 3 online members */}
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center -space-x-2 hover:opacity-80 transition-opacity relative"
        data-testid="team-members-avatars"
      >
        {displayMembers.length > 0 ? (
          <>
            {displayMembers.map((member, index) => (
              <div 
                key={member.id} 
                className="relative"
                style={{ zIndex: 3 - index }}
              >
                <Avatar className={`h-8 w-8 border-2 border-border dark:border-zinc-700 ${getAvatarColor(member.name)}`}>
                  <AvatarFallback className="text-white text-xs font-medium">
                    {getInitials(member.name)}
                  </AvatarFallback>
                </Avatar>
                {/* Online indicator */}
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-card rounded-full" />
              </div>
            ))}
            {extraCount > 0 && (
              <div 
                className="relative flex items-center justify-center h-8 w-8 rounded-full bg-muted border-2 border-border dark:border-zinc-700 text-xs font-medium"
                style={{ zIndex: 0 }}
              >
                +{extraCount}
              </div>
            )}
            {/* Unread DM indicator */}
            {unreadDmCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center z-10" data-testid="dm-unread-badge">
                {unreadDmCount > 9 ? '9+' : unreadDmCount}
              </span>
            )}
          </>
        ) : (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/50 text-muted-foreground text-sm relative">
            <Users className="w-4 h-4" />
            <span>{totalMembers} members</span>
            {/* Unread DM indicator for no online members */}
            {unreadDmCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center" data-testid="dm-unread-badge">
                {unreadDmCount > 9 ? '9+' : unreadDmCount}
              </span>
            )}
          </div>
        )}
      </button>

      {/* Team Members Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Team Members
              <Badge variant="secondary" className="ml-2">
                {totalOnline} online
              </Badge>
            </DialogTitle>
          </DialogHeader>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="member-search-input"
            />
          </div>

          {/* Members List */}
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-2">
              {filteredMembers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No members found
                </div>
              ) : (
                filteredMembers.map((member) => {
                  const memberUnread = getUnreadCountForMember(member.id);
                  return (
                  <div
                    key={member.id}
                    className={`flex items-center justify-between p-3 rounded-lg hover:bg-accent/50 transition-colors ${memberUnread > 0 ? 'bg-primary/5 border border-primary/20' : ''}`}
                    data-testid={`member-${member.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <Avatar className={`h-10 w-10 ${getAvatarColor(member.name)}`}>
                          <AvatarFallback className="text-white font-medium">
                            {getInitials(member.name)}
                          </AvatarFallback>
                        </Avatar>
                        {member.is_online && (
                          <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-background rounded-full" />
                        )}
                        {memberUnread > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                            {memberUnread > 9 ? '9+' : memberUnread}
                          </span>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{member.name}</p>
                          {memberUnread > 0 && (
                            <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                              {memberUnread} new
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{member.email}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Circle 
                            className={`w-2 h-2 ${member.is_online ? 'fill-green-500 text-green-500' : 'fill-gray-400 text-gray-400'}`} 
                          />
                          <span className={`text-xs ${member.is_online ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
                            {member.is_online ? 'Online' : 'Offline'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant={memberUnread > 0 ? "default" : "ghost"}
                      size="sm"
                      onClick={() => handleStartChat(member)}
                      data-testid={`chat-with-${member.id}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                      {memberUnread > 0 && <span className="ml-1">View</span>}
                    </Button>
                  </div>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TeamMembersAvatars;
